/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Match Booster Offerings", description = "Increase match rates for your audiences and conversion events in advertising platforms.", lastUpdatedDate = new Date(1695254400000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Match Booster Offerings",
        description: "Increase match rates for your audiences and conversion events in advertising platforms.",
        lastUpdatedDate: "Thu Sep 21 2023 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Audience boosting",
          "depth": 0,
          "id": "audience-boosting"
        }, {
          "value": "Overview",
          "depth": 1,
          "id": "overview"
        }, {
          "value": "Use cases",
          "depth": 1,
          "id": "use-cases"
        }, {
          "value": "Conversion event boosting",
          "depth": 0,
          "id": "conversion-event-boosting"
        }, {
          "value": "Overview",
          "depth": 1,
          "id": "overview-1"
        }, {
          "value": "Use cases",
          "depth": 1,
          "id": "use-cases-1"
        }, {
          "value": "Anonymous targeting",
          "depth": 0,
          "id": "anonymous-targeting"
        }, {
          "value": "Overview",
          "depth": 1,
          "id": "overview-2"
        }, {
          "value": "Use cases",
          "depth": 1,
          "id": "use-cases-2"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      strong: "strong",
      h2: "h2",
      h3: "h3",
      ul: "ul",
      li: "li"
    }, _provideComponents(), props.components), {BtOnly} = _components;
    if (!BtOnly) _missingMdxReference("BtOnly", true);
    return _jsxs(_Fragment, {
      children: [_jsx(BtOnly, {
        featureName: "Match Booster",
        extraText: " as an add-on product."
      }), "\n", _jsxs(_components.p, {
        children: ["There are several offerings under the ", _jsx(_components.strong, {
          children: "Match Booster"
        }), " product line."]
      }), "\n", _jsx(_components.h2, {
        id: "audience-boosting",
        children: "Audience boosting"
      }), "\n", _jsx(_components.h3, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "Consumer data"
        })
      }), "\n", _jsx(_components.p, {
        children: "The core Match Booster offering is boosting consumer segments and audiences. You may be collecting emails or phone numbers from consumers, yet those identifiers have low match rates in ad platforms. Audience boosting of consumer data entails enriching your first-party data with additional emails, phones, device IDs, and cookie IDs to increase match rates."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "Work data"
        })
      }), "\n", _jsx(_components.p, {
        children: "Match Booster also supports boosting of B2B segments and audiences. You may be collecting work emails from prospects or have purchased a list of prospects from a third party. However, audience match rates of those emails are low. Audience boosting of work data entails enriching your first-party data with additional consumer emails, phones, device IDs, and cookie IDs to increase match rates."
      }), "\n", _jsx(_components.h3, {
        id: "use-cases",
        children: "Use cases"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Reach expansion: increase audience size to avoid over-bidding on a small segment"
        }), "\n", _jsx(_components.li, {
          children: "Custom campaigns: increase audience size to meet minimum platform requirements for custom audiences"
        }), "\n", _jsx(_components.li, {
          children: "Suppression: better suppress ads on existing customers or low-likelihood audiences"
        }), "\n", _jsx(_components.li, {
          children: "Lookalikes: increase precision of seeds for lookalike audiences"
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "conversion-event-boosting",
        children: "Conversion event boosting"
      }), "\n", _jsx(_components.h3, {
        id: "overview-1",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Match Booster supports boosting of offline conversion events. Whether you're running a targeted campaign or a broad campaign, you may be collecting conversion events (eg., purchased item, requested demo) that is not captured by the ad platforms themselves. Conversion event boosting entails enriching your first-party data with additional emails and phones to increase match rates."
      }), "\n", _jsx(_components.p, {
        children: "Note: Conversion event boosting does not cause the same event to be sent into the ad platform multiple times."
      }), "\n", _jsx(_components.h3, {
        id: "use-cases-1",
        children: "Use cases"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Attribution and measurement: enhance attribution and ROAS analysis for your ad campaigns"
        }), "\n", _jsx(_components.li, {
          children: "Better targeting: improve accuracy of conversion signals"
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "anonymous-targeting",
        children: "Anonymous targeting"
      }), "\n", _jsx(_components.h3, {
        id: "overview-2",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Match Booster supports boosting of anonymous visitors via IP addresses. You may be collecting user events and their associated IP addresses, even if the user does not provide personally identifiable information like emails and phone numbers. Hightouch and our data partners are able to identify the most common emails and device IDs associated with an IP addresss. Anonymous targeting entails enhancing your first-party IP address data with additioanl email and device IDs to increase match rates."
      }), "\n", _jsx(_components.p, {
        children: "Note: Hightouch rigorously cleans observed linkages between IP addresses, HEMs, and MAIDs to eliminate public locations."
      }), "\n", _jsx(_components.h3, {
        id: "use-cases-2",
        children: "Use cases"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Web retargeting: retargeting anonymous visitors to your web properties"
        }), "\n", _jsx(_components.li, {
          children: "Household targeting: retarget households based on IP address"
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
